/** Cross-cutting constants that both the client and the server must agree on. */

/** Default rounds per match. */
export const DEFAULT_ROUND_COUNT = 7;

/** Lead time between round.arm and startAtServerTs (spec 8.2). */
export const ROUND_ARM_LEAD_MS = 500;

/** Clock-sync cadence and the rolling sample window (spec 8.1). */
export const CLOCK_SYNC_INTERVAL_MS = 15_000;
export const CLOCK_SYNC_SAMPLE_WINDOW = 8;

/** Jitter tolerance for the latency-fair clamp (spec 8.3). */
export const JITTER_ALLOWANCE_MS = 30;

/**
 * Hard ceiling on any RTT estimate. One pathological sample cannot open a floor
 * or a window beyond this, and the estimator clamps every measurement to it.
 */
export const MAX_RTT_MS = 2_000;

/**
 * Ceiling on the round trip the server will PAY FOR IN POINTS — the 8.3 clamp's
 * credited floor, and the reveal instant a rung's latency is measured from.
 *
 * RTT is server-measured, so a client cannot simply assert a big one. What it
 * CAN do is stall its control-frame pongs and drag the measurement upwards, and
 * every millisecond of measured RTT buys it half a millisecond of looser floor
 * and half a millisecond of later reveal instant. This cap bounds that lever:
 * above it a connection is not credited for the excess.
 *
 * WHAT IT COSTS THE HONEST SIDE, measured rather than asserted. The credit
 * applies to TWO hops, not one — the floor credits the answer's flight home and
 * the reveal instant credits the key's flight out — so a seat whose true round
 * trip is R has its scored latency inflated by exactly `R - CREDITED_RTT_CAP_MS`
 * once R passes the cap, not by `(R - cap)/2`. Driving the real MatchRoom with
 * truthful clients and an identical 2000ms think:
 *
 *      true RTT     scored latency     overcharge
 *          20ms              2000              0ms
 *         400ms              2000              0ms
 *         800ms              2400            400ms
 *        1200ms              2800            800ms
 *
 * An earlier version of this comment claimed "a 400ms player is scored as if
 * they thought for 200ms longer", which is wrong twice over: it is zero at 400ms
 * (the cap) and it is the full excess, not half of it, above the cap. The number
 * is bounded by SPEED_BONUS_MAX either way — at Navigator rung 4 the worst case
 * measured is 2640 points against 2760 for identical thinking, which is the
 * price of not taxing every genuinely slow player on every single round. Spec
 * 8.7's answer to the remaining residue is the anomaly log and a review
 * pipeline, not a false-positive-prone hard reject.
 *
 * It lives in @geogame/shared because the client applies the identical credit
 * to its own optimistic deadline (invariant 6): if the two sides capped
 * differently, one of them would cut the player off while the other was still
 * accepting input.
 */
export const CREDITED_RTT_CAP_MS = 400;

/** RTT as the clamp and the reveal instant are allowed to see it. */
export function creditedRttMs(rtt: number): number {
  if (!Number.isFinite(rtt) || rtt <= 0) return 0;
  return Math.min(rtt, CREDITED_RTT_CAP_MS);
}

/**
 * Ceiling on the round trip the server will pay for IN TIME — how far the
 * deeper-rung deadline is advanced so the window opens when the key lands.
 *
 * THIS IS A DIFFERENT CAP TO `CREDITED_RTT_CAP_MS`, ON PURPOSE, AND IT IS THE
 * WHOLE FIX FOR THE ONE PLACE INVARIANT 2 WAS ACTUALLY BREAKING.
 *
 * Both caps used to be the same number, which quietly priced two very different
 * failures the same way. Under-crediting POINTS costs a slow player some speed
 * bonus: bounded, soft, and the documented trade above. Under-crediting the
 * WINDOW costs them the round. A push credits `rungMs + credit` of deadline and
 * the key does not land for another `R/2`, so the readable window is
 * `rungMs + credit - R` — at R = 800ms and a 400ms credit that is 9600ms of a
 * 10000ms rung, and a player who reads for 9750ms of a window they were told
 * was ten seconds is answered with `round_closed`. They bust and lose the
 * round's whole pot for owning a satellite link. Measured on a real server
 * behind a real TCP lag proxy: at 20ms and 200ms the deeper rung is ACCEPTED,
 * at 800ms it is REJECTED, for the identical play.
 *
 * So the window is credited at the full measured round trip, bounded only by
 * `MAX_RTT_MS` — which is where the estimator clamps anyway, so in practice the
 * window credit is simply "the RTT we measured". No honest link, however bad,
 * is ever cut off for its pipe again.
 *
 * WHAT THAT HANDS A PONG-STALLER, stated plainly because the old comment
 * measured this exploit only in rung-1 points and missed the half that matters.
 * Stalling buys (a) up to `CREDITED_RTT_CAP_MS` of shaved scored latency, worth
 * ~10 points over three rounds against an honest 540 — that part is capped and
 * unchanged — and (b) up to `MAX_RTT_MS` of extra real thinking time on every
 * deeper rung, which was 400ms and is now up to 2s. That is the deliberate
 * trade: a bounded, LOGGED advantage for a cheat against a certain, silent loss
 * for an honest slow player. `MatchRoom` raises `window_credit_above_cap` on
 * every answer from a seat credited more window than `CREDITED_RTT_CAP_MS`, so
 * the population that can be buying time is exactly the population spec 8.7's
 * review pipeline sees.
 */
export const WINDOW_RTT_CREDIT_CAP_MS = MAX_RTT_MS;

/** RTT as the deeper-rung deadline credit is allowed to see it. */
export function windowCreditMs(rtt: number): number {
  if (!Number.isFinite(rtt) || rtt <= 0) return 0;
  return Math.min(rtt, WINDOW_RTT_CREDIT_CAP_MS);
}

/** Anomaly flag (spec 8.7) raised when a seat is credited window above the cap. */
export const WINDOW_CREDIT_FLAG = 'window_credit_above_cap';

/** Disconnect grace before forfeit (spec 8.6). The round clock never pauses. */
export const DISCONNECT_GRACE_MS = 20_000;

/** Seconds of thin queue before the ghost fallback engages (spec section 9). */
export const GHOST_FALLBACK_MS = 8_000;

/** Number of options presented for a multiple-choice probe. */
export const PROBE_OPTION_COUNT = 4;

/**
 * Population brackets used by the `population` probe. Upper bounds, exclusive.
 *
 * The boundaries are deliberately OFF the round numbers people memorise.
 * They used to sit at 1M / 5M / 10M / 25M / 50M / 100M / 250M, and 30 of the
 * 194 scorable countries landed within 8% of an edge — Greece (10.40M) and
 * Hungary (9.60M) are both "about ten million" to a player and fell in
 * DIFFERENT brackets. `populationProbe` preferentially offers the ±1 brackets,
 * so the two candidates a torn player was choosing between were exactly the two
 * on the table: a rounding trap, not a difficulty knob.
 *
 * These edges were chosen by minimising, over the baked dataset, the number of
 * countries within 8% of any boundary subject to log-ish spacing (each edge
 * 2.1×–4.3× the previous) and no bracket holding more than 42 countries.
 * Result: 8 countries within 8% and 6 within 5%, down from 30 and 20.
 *
 * `packages/data/src/dataset.test.ts` asserts the near-boundary count stays
 * low, so a population edit that reintroduces the trap fails the gate. (The
 * test lives in @geogame/data because only that package holds the figures;
 * @geogame/shared must not depend on it.)
 */
export const POPULATION_BRACKETS: readonly {
  readonly label: string;
  readonly min: number;
  readonly max: number;
}[] = [
  { label: 'under 1.1 million', min: 0, max: 1_100_000 },
  { label: '1.1–3.1 million', min: 1_100_000, max: 3_100_000 },
  { label: '3.1–7.6 million', min: 3_100_000, max: 7_600_000 },
  { label: '7.6–16 million', min: 7_600_000, max: 16_000_000 },
  { label: '16–42 million', min: 16_000_000, max: 42_000_000 },
  { label: '42–160 million', min: 42_000_000, max: 160_000_000 },
  { label: '160–390 million', min: 160_000_000, max: 390_000_000 },
  { label: 'over 390 million', min: 390_000_000, max: Number.MAX_SAFE_INTEGER },
];

/** Index into POPULATION_BRACKETS for a raw population figure. */
export function populationBracketIndex(population: number): number {
  for (let i = 0; i < POPULATION_BRACKETS.length; i += 1) {
    const b = POPULATION_BRACKETS[i];
    if (b && population < b.max) return i;
  }
  return POPULATION_BRACKETS.length - 1;
}

/** Glicko-2 defaults for a fresh anonymous player (spec section 7). */
export const GLICKO2_DEFAULTS = {
  rating: 1500,
  rd: 350,
  sigma: 0.06,
  tau: 0.5,
} as const;

// ---------------------------------------------------------------------------
// Round asset bundles (invariants 2 and 5)
// ---------------------------------------------------------------------------

/**
 * Padded sizes, in bytes, that a round's sealed asset bundle is allowed to be.
 *
 * WHY A LADDER AT ALL. A flag rung's artwork is vendored and committed, so its
 * byte length is a public, stable fingerprint of the country. Serve four flags
 * at their natural sizes and `Content-Length` IS the answer key — cheaper to
 * read than the ISO-coded URL this whole change removed, because it needs no
 * image recognition, just a table anybody can build from the repository. So the
 * four flags travel as ONE response, gzipped, padded up to a fixed rung of this
 * ladder, and sealed. Two properties fall out:
 *
 *   - WITHIN a question, nothing distinguishes the options. There is one
 *     response, one length, no per-option request, and no ordering. The
 *     brief's "fetch all four in shuffled order, same sizes if feasible" is
 *     satisfied more strongly by fetching them as one thing.
 *   - ACROSS questions, a length reveals only which rung of a four-rung ladder
 *     the four flags COMBINED landed in — measured 57.6% / 20.7% / 21.1% /
 *     0.6% over 14k generated flag questions, so the smallest bucket alone
 *     holds thousands of possible sets, and it never says which option is
 *     which.
 *
 * The first three rungs are sized from that same measurement of real generated
 * questions: gzipped bundles run p50 2.8 KiB, p99 53.8 KiB, observed max 106.0
 * KiB. Mean cost on the wire is 20.3 KiB per flag round.
 *
 * THE TOP RUNG IS NOT SIZED FROM THE OBSERVED MAXIMUM, and that distinction
 * cost a red test to learn. The distractor draw can fall through to the world
 * stratum, so nothing forbids a question from putting Afghanistan, Bolivia,
 * Mexico and Spain — the four heaviest coats of arms in the set — in front of
 * one player. That bundle is 144.6 KiB gzipped, half again the largest one in
 * 14k sampled questions, and a ladder sized to the sample would have thrown
 * mid-round on a draw nobody had seen yet. 192 KiB covers the exhaustive worst
 * case with a third to spare, and `assets.test.ts` recomputes that worst case
 * from the committed artwork so a re-vendor cannot outgrow it quietly.
 *
 * 192 KiB is also 393ms at a pessimistic 4 Mbit/s, so even the worst bundle
 * fits inside ROUND_ARM_LEAD_MS — and flags sit on the TERMINAL rung, so the
 * real budget is that window plus the whole rung-1 clock in front of it.
 */
export const ASSET_BUNDLE_LADDER_BYTES: readonly number[] = [4_096, 16_384, 65_536, 196_608];

/** The smallest ladder rung that holds `n` bytes, or null when nothing does. */
export function assetBundleRung(n: number): number | null {
  for (const rung of ASSET_BUNDLE_LADDER_BYTES) {
    if (n <= rung) return rung;
  }
  return null;
}

/**
 * How long an asset token stays resolvable after the round it belongs to has
 * ended. Covers the reveal card, a reconnect mid-round, and a browser that
 * re-requests an image it already had. Short, because the token is a capability
 * and a capability that outlives its round is a capability somebody can share.
 */
export const ASSET_TOKEN_GRACE_MS = 30_000;
